// Pure helpers for layer batch + housing stat derivation. Extracted from
// LayerBatchesView so the upcoming layer.batch / layer.housing record-page
// migration only needs to touch UI, routing, and Activity wiring.
//
// All helpers are pure: input rows in, computed object out. They do not
// fetch, persist, mutate, or touch Activity. They preserve the source
// records, date boundaries, batch_id attribution, and feed phase math the
// view used inline before the extraction.

export const HOUSING_CAPS = {
  'Layer Schooner': 450,
  'Eggmobile 1': 250,
  'Eggmobile 2': 250,
  'Eggmobile 3': 250,
  'Eggmobile 4': 250,
  'Retirement Home': 9999,
};

const HOUSING_CAP_FALLBACK = 9999;

// A layer batch row, reduced to the fields these derivations read. `id` is a
// uuid; the two anchor columns are ISO 'YYYY-MM-DD' dates.
export type LayerBatchRow = {
  id: string;
  brooder_entry_date?: string | null;
  arrival_date?: string | null;
};

// A layer housing row. start_date / retired_date bound the attribution window;
// an empty value on either side means open-ended.
export type LayerHousingRow = {
  id: string;
  batch_id?: string | null;
  housing_name?: string | null;
  start_date?: string | null;
  retired_date?: string | null;
};

// One layer_dailys row. feed_lbs / mortality_count arrive as numeric strings
// from the webform and as numbers from the app, which is why both readers parse.
export type LayerDailyRow = {
  batch_id?: string | null;
  batch_label?: string | null;
  date?: string | null;
  feed_lbs?: string | number | null;
  mortality_count?: string | number | null;
  feed_type?: string | null;
};

// One egg_dailys row: up to four (group name, count) pairs per day.
export type EggDailyRow = {
  date?: string | null;
  group1_name?: string | null;
  group1_count?: string | number | null;
  group2_name?: string | null;
  group2_count?: string | number | null;
  group3_name?: string | null;
  group3_count?: string | number | null;
  group4_name?: string | null;
  group4_count?: string | number | null;
};

// The five feed/mortality/egg totals every stat bucket carries.
export type LayerStatTotals = {
  totalFeed: number;
  totalMort: number;
  totalEggs: number;
  starterFeed: number;
  growerFeed: number;
  layerFeed: number;
};

// The three feed phases the bird-age calc resolves to.
export type LayerFeedPhase = string;

export function getHousingCap(name: string | null | undefined): number {
  if (!name) return HOUSING_CAP_FALLBACK;
  const lower = String(name).toLowerCase();
  for (const [k, v] of Object.entries(HOUSING_CAPS)) {
    if (lower.includes(k.toLowerCase())) return v;
  }
  return HOUSING_CAP_FALLBACK;
}

// Date is in a housing's active window. Empty start/end means open-ended on
// that side. Caller passes ISO date strings ('YYYY-MM-DD') so lexical
// comparison is correct.
export function inRange(
  date: string | null | undefined,
  start: string | null | undefined,
  end: string | null | undefined,
): boolean {
  if (!date) return false;
  if (start && date < start) return false;
  if (end && date > end) return false;
  return true;
}

// Bird-age feed phase. Anchor is the brooder entry date (falls back to
// arrival_date at the call site). Thresholds: 0-20 STARTER, 21-139 GROWER,
// 140+ LAYER. If anchor is missing, fall back to the stored feed_type so
// historical rows without an anchor keep their recorded phase.
//
// `storedType` is OPTIONAL, not merely nullable. `string | null | undefined`
// admits undefined as a VALUE while still REQUIRING the argument, so the two
// two-argument calls this module's own suite asserts — calcPhaseFromAge(date,
// null) and calcPhaseFromAge(null, anchor), both expecting the 'LAYER'
// fallback — were arity errors that nothing reported while the suite was
// JavaScript. The runtime behaviour is unchanged: an omitted third argument is
// undefined either way, and `storedType || 'LAYER'` already answers it.
export function calcPhaseFromAge(
  reportDate: string | null | undefined,
  brooderEntry: string | null | undefined,
  storedType?: string | null,
): LayerFeedPhase {
  if (!brooderEntry || !reportDate) return storedType || 'LAYER';
  try {
    // `.getTime()` is what the `-` operator already did: subtracting two Dates
    // coerces each through valueOf(), which returns exactly this number.
    const days = Math.floor(
      (new Date(reportDate + 'T12:00:00').getTime() - new Date(brooderEntry + 'T12:00:00').getTime()) / 86400000,
    );
    if (days < 21) return 'STARTER';
    if (days < 140) return 'GROWER';
    return 'LAYER';
  } catch (_e) {
    return storedType || 'LAYER';
  }
}

// Sum the egg counts on one egg_daily row that match a given housing_name.
function sumEggsForHousing(eggDaily: EggDailyRow, housingName: string | null | undefined): number {
  let e = 0;
  const pairs = [
    [eggDaily.group1_name, eggDaily.group1_count],
    [eggDaily.group2_name, eggDaily.group2_count],
    [eggDaily.group3_name, eggDaily.group3_count],
    [eggDaily.group4_name, eggDaily.group4_count],
  ];
  for (const [n, c] of pairs) {
    if (n === housingName) e += parseInt(String(c)) || 0;
  }
  return e;
}

// Batch stats: keyed by batch.id. Attribution is batch_id-based on
// layer_dailys (NOT batch_label text matching). Eggs are summed from
// egg_dailys per attached housing, bounded by each housing's
// start_date/retired_date window.
export function computeBatchStats(
  layerBatches: LayerBatchRow[] | null | undefined,
  layerHousings: LayerHousingRow[] | null | undefined,
  layerDailys: LayerDailyRow[] | null | undefined,
  eggDailys: EggDailyRow[] | null | undefined,
): Record<string, LayerStatTotals> {
  const stats: Record<string, LayerStatTotals> = {};
  for (const batch of layerBatches || []) {
    const anchor = batch.brooder_entry_date || batch.arrival_date || null;
    const bHousings = (layerHousings || []).filter((h) => h.batch_id === batch.id);
    const myReports = (layerDailys || []).filter((d) => d.batch_id === batch.id);
    let totalFeed = 0;
    let totalMort = 0;
    let starterFeed = 0;
    let growerFeed = 0;
    let layerFeed = 0;
    for (const d of myReports) {
      const f = parseFloat(String(d.feed_lbs)) || 0;
      totalFeed += f;
      totalMort += parseInt(String(d.mortality_count)) || 0;
      const phase = calcPhaseFromAge(d.date, anchor, d.feed_type);
      if (phase === 'STARTER') starterFeed += f;
      else if (phase === 'GROWER') growerFeed += f;
      else layerFeed += f;
    }
    let totalEggs = 0;
    for (const h of bHousings) {
      for (const d of eggDailys || []) {
        if (!inRange(d.date, h.start_date, h.retired_date)) continue;
        totalEggs += sumEggsForHousing(d, h.housing_name);
      }
    }
    stats[batch.id] = {totalFeed, totalMort, totalEggs, starterFeed, growerFeed, layerFeed};
  }
  return stats;
}

// Housing stats: keyed by housing.id. layer_dailys are matched to a housing
// by case-insensitive trimmed batch_label vs housing_name AND by date being
// inside the housing's active window. The parent batch supplies the
// brooder/arrival anchor used by the phase calc.
export function computeHousingStats(
  layerBatches: LayerBatchRow[] | null | undefined,
  layerHousings: LayerHousingRow[] | null | undefined,
  layerDailys: LayerDailyRow[] | null | undefined,
  eggDailys: EggDailyRow[] | null | undefined,
): Record<string, LayerStatTotals> {
  const batchById: Record<string, LayerBatchRow | undefined> = Object.fromEntries(
    (layerBatches || []).map((b) => [b.id, b]),
  );
  const hStats: Record<string, LayerStatTotals> = {};
  for (const h of layerHousings || []) {
    const parent = h.batch_id ? batchById[h.batch_id] : null;
    const anchor = parent ? parent.brooder_entry_date || parent.arrival_date || null : null;
    const hd = (layerDailys || []).filter(
      (d) =>
        String(d.batch_label || '')
          .toLowerCase()
          .trim() ===
          String(h.housing_name || '')
            .toLowerCase()
            .trim() && inRange(d.date, h.start_date, h.retired_date),
    );
    let totalFeed = 0;
    let totalMort = 0;
    let starterFeed = 0;
    let growerFeed = 0;
    let layerFeed = 0;
    for (const d of hd) {
      const f = parseFloat(String(d.feed_lbs)) || 0;
      totalFeed += f;
      totalMort += parseInt(String(d.mortality_count)) || 0;
      const phase = calcPhaseFromAge(d.date, anchor, d.feed_type);
      if (phase === 'STARTER') starterFeed += f;
      else if (phase === 'GROWER') growerFeed += f;
      else layerFeed += f;
    }
    let totalEggs = 0;
    for (const d of eggDailys || []) {
      if (!inRange(d.date, h.start_date, h.retired_date)) continue;
      totalEggs += sumEggsForHousing(d, h.housing_name);
    }
    hStats[h.id] = {
      totalFeed,
      totalMort,
      totalEggs,
      starterFeed,
      growerFeed,
      layerFeed,
    };
  }
  return hStats;
}
